/* eslint-disable */
"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/hooks/auth-context";
import {
    doCreateUserWithEmailAndPassword,
    doSignInWithEmailAndPassword,
    doSendEmailVerification,
    doSignInWithGoogle,
    checkNickname,
    doInsertDisplayName,
    insertUserHasura,
    doSignInWithApple,
    checkUserEmail,
    doPasswordReset, doSignOut,
} from "@/services/auth";
import LoginFields from "@/components/user/LoginFields";
import RegisterFields from "@/components/user/RegisterFields";
import { getFirebaseErrorMessage } from "@/lib/utils";
import { utilsService } from "@/services/utils";

const LoginComponent = ({ onClose, registration, isCheckoutFlow, onCheckoutReady }: any) => {
    const { userLoggedIn, currentUser } = useAuth();
    const pathname = usePathname();

    // Funzione per preparare i dati delle preferenze utente
    const prepareUserPreferences = async (preferences: any) => {
        try {
            const now = new Date().toISOString();
            const { privacyVersion, termsVersion, analyticsVersion } = await utilsService.getCurrentVersions();

            return {
                terms_and_conditions: preferences.termsAndConditions,
                privacy_policy: preferences.privacyPolicy,
                marketing_emails: preferences.marketingEmails,
                privacy_version: privacyVersion,
                terms_version: termsVersion,
                marketing_emails_updated_at: preferences.marketingEmails ? now : null,
                ambassador_program: preferences.ambassadorProgram,
                ambassador_program_updated_at: preferences.ambassadorProgram ? now : null,
                analytics: preferences.analytics,
                analytics_version: analyticsVersion,
                analytics_updated_at: preferences.analytics ? now : null,
            };
        } catch (error) {
            console.error('Errore nel preparare le preferenze:', error);
            // Fallback con valori di default
            return {
                terms_and_conditions: preferences.termsAndConditions,
                privacy_policy: preferences.privacyPolicy,
                marketing_emails: preferences.marketingEmails,
                privacy_version: '1',
                terms_version: '1',
                marketing_emails_updated_at: preferences.marketingEmails ? new Date().toISOString() : null,
                ambassador_program: preferences.ambassadorProgram,
                ambassador_program_updated_at: preferences.ambassadorProgram ? new Date().toISOString() : null,
                analytics: preferences.analytics,
                analytics_version: '1',
                analytics_updated_at: preferences.analytics ? new Date().toISOString() : null,
            };
        }
    };

    // Campi originali per la logica
    const [email, setEmail] = useState("");
    // Nickname fittizio per Hasura (non lo chiediamo all’utente)
    const [nickname, setNickname] = useState("");
    const [password, setPassword] = useState("");

    // Campi del 3° step (soltanto UI, non usati nella logica)
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [isAdult, setIsAdult] = useState(false);

    // Nuovi stati per le preferenze utente
    const [termsAndConditions, setTermsAndConditions] = useState(false);
    const [privacyPolicy, setPrivacyPolicy] = useState(false);
    const [marketingEmails, setMarketingEmails] = useState(false);
    const [ambassadorProgram, setAmbassadorProgram] = useState(false);
    const [analytics, setAnalytics] = useState(false);

    // Stati per il social login
    const [socialUser, setSocialUser] = useState<any>(null);
    const [socialEmail, setSocialEmail] = useState("");
    const [socialFirstName, setSocialFirstName] = useState("");
    const [socialLastName, setSocialLastName] = useState("");
    const [socialProvider, setSocialProvider] = useState(""); // "google" o "apple"
    const [socialPhotoUrl, setSocialPhotoUrl] = useState(""); // avatar da Google

    const [isSigningIn, setIsSigningIn] = useState(false);
    const [isLogin, setIsLogin] = useState(true);
    const [isRegistration, setIsRegistration] = useState(false);
    const [isSigningUp, setIsSigningUp] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");


    // Stato di layout per il login
    const [step, setStep] = useState<"email" | "password" | "resetPassword">("email");
    // Stato di layout per la registrazione: 6 step -> "email", "profile", "social_preferences", "confirmation", "confirmed", "checkout_success"
    const [regStep, setRegStep] = useState<
        "email" | "profile" | "social_preferences" | "confirmation" | "confirmed" | "checkout_success"
    >("email");


    // Se arriva la prop “registration”, passiamo in modalità Registrazione
    useEffect(() => {
        if (registration) {
            setIsLogin(false);
            setIsRegistration(true);
        }
    }, [registration]);

    // Se siamo in checkout flow e regStep è "confirmation", mostra il dialog di successo checkout
    useEffect(() => {
        if ((isCheckoutFlow || pathname === "/shop/checkout") && regStep === "confirmation") {
            setRegStep("checkout_success");
        }
    }, [pathname, regStep, isCheckoutFlow]);

    // Quando l'utente è registrato e siamo in checkout flow, procedi automaticamente
    useEffect(() => {
        if (regStep === "checkout_success" && userLoggedIn && currentUser && isCheckoutFlow && onCheckoutReady) {
            // Aspetta un attimo per mostrare il messaggio di successo, poi procedi
            const timer = setTimeout(() => {
                console.log('Registrazione completata, chiamando onCheckoutReady', { 
                    userLoggedIn, 
                    hasCurrentUser: !!currentUser, 
                    uid: currentUser?.uid 
                });
                onClose();
                // Aspetta ancora un attimo prima di chiamare onCheckoutReady per dare tempo al contesto di aggiornarsi
                setTimeout(() => {
                    onCheckoutReady();
                }, 300);
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [regStep, userLoggedIn, currentUser, isCheckoutFlow, onCheckoutReady, onClose]);

    // Quando l'utente si logga (non registra) e siamo in checkout flow, procedi automaticamente
    useEffect(() => {
        if (userLoggedIn && currentUser && isCheckoutFlow && onCheckoutReady && isLogin && !isRegistration) {
            // Aspetta un po' di più per assicurarsi che il login sia completamente completato
            // e che currentUser sia disponibile nel contesto
            const timer = setTimeout(() => {
                console.log('Login completato, chiamando onCheckoutReady', { 
                    userLoggedIn, 
                    hasCurrentUser: !!currentUser, 
                    uid: currentUser?.uid 
                });
                onClose();
                // Aspetta ancora un attimo prima di chiamare onCheckoutReady per dare tempo al contesto di aggiornarsi
                setTimeout(() => {
                    onCheckoutReady();
                }, 300);
            }, 1000); // Aumentato a 1 secondo
            return () => clearTimeout(timer);
        }
    }, [userLoggedIn, currentUser, isCheckoutFlow, onCheckoutReady, onClose, isLogin, isRegistration]);

    // onSubmit invariato: login o registrazione
    const onSubmit = async (e: any) => {
        e.preventDefault();
        setErrorMessage("");
        if (isLogin) {
            if (password === "" || password.length < 6) {
                setErrorMessage("Compila tutti i campi correttamente");
                return
            }
            const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            if (!emailRegex.test(email)) {
                setErrorMessage("Inserisci un indirizzo email valido");
                return;
            }
            // LOGICA LOGIN
            setIsSigningIn(true);
            try {
                await doSignInWithEmailAndPassword(email, password);
                // Se siamo in checkout flow, procedi con il checkout invece di solo chiudere
                if (isCheckoutFlow && onCheckoutReady) {
                onClose();
                    // Aspetta un attimo per assicurarsi che l'utente sia loggato
                    setTimeout(() => {
                        onCheckoutReady();
                    }, 500);
                } else {
                onClose();
                }
            } catch (error: any) {
                setIsSigningIn(false);
                const message = getFirebaseErrorMessage(error.code);
                setErrorMessage(message);
            }
        } else if (isRegistration) {
            // LOGICA REGISTRAZIONE
            if (!isAdult) {
                setErrorMessage("Devi dichiarare di essere maggiorenne");
                setIsSigningUp(false);
                return false;
            }

            // Validazione checkbox obbligatori
            if (!termsAndConditions) {
                setErrorMessage("Devi accettare i termini e condizioni");
                setIsSigningUp(false);
                return false;
            }

            if (!privacyPolicy) {
                setErrorMessage("Devi accettare l'informativa sulla privacy");
                setIsSigningUp(false);
                return false;
            }

            const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            if (!emailRegex.test(email)) {
                setErrorMessage("Inserisci un indirizzo email valido");
                setIsSigningUp(false);
                return false;
            }

            if (password === "" || password.length < 6) {
                setErrorMessage("La password deve essere di almeno 6 caratteri");
                setIsSigningUp(false);
                return false;
            }
            setErrorMessage("");
            setIsSigningUp(true);

            try {
                // Verifica se nickname è già usato
                const usedNickname = await checkNickname(nickname);
                if (usedNickname) {
                    setErrorMessage("Riprova con un nickname diverso");
                    setIsSigningUp(false);
                    return;
                }
                // Crea l’utente
                await doCreateUserWithEmailAndPassword(email, password);
                doSendEmailVerification();
                // Inserisce nickname
                await doInsertDisplayName(nickname.trim());
                // Prepara le preferenze utente
                const userPreferences = await prepareUserPreferences({
                    termsAndConditions,
                    privacyPolicy,
                    marketingEmails,
                    ambassadorProgram,
                    analytics
                });

                // Inserisce utente su Hasura con le preferenze
                let id = await insertUserHasura(email, nickname.trim(), firstName || null, lastName || null, userPreferences, null);
                if (id) {
                    // Se siamo in checkout flow, mostra il dialog di successo checkout e procedi
                    if (isCheckoutFlow || pathname === "/shop/checkout") {
                        setIsSigningUp(false);
                        setRegStep("checkout_success");
                        return;
                    }
                    // Step 4: "confirmation" (Email inviata)
                    setRegStep("confirmation");
                    setIsSigningUp(false);
                }

                // Se l’email è già confermata (o appena confermata), passiamo allo step 5
                let emailConf = await checkUserEmail(); // logica tua: controlla se l’utente ha già confermato
                if (emailConf) {
                    setRegStep("confirmed");
                }
            } catch (error: any) {
                setIsSigningUp(false);
                const message = getFirebaseErrorMessage(error.code);
                setErrorMessage(message);
                doSignOut()
            }
        }
    };

    // Funzione per verificare se un utente esiste già
    const checkUserExists = async (firebaseId: string) => {
        try {
            const basePath = process.env.NEXT_PUBLIC_HASURA_BASEPATH || 'https://dev-api.babalucloud.it';
            const graphqlUrl = `${basePath}/v1/graphql`;

            const response = await fetch(graphqlUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    query: `query getUser($firebaseId: String!) {
                        users(where: {firebaseId: {_eq: $firebaseId}}) {
                            id
                        }
                    }`,
                    variables: {
                        firebaseId
                    }
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to fetch utils');
            }

            const result = await response.json();
            return result.data.users.length > 0;
        } catch (error) {
            console.error("Errore nel controllo esistenza utente:", error);
            return false;
        }
    };

    // Social login con controllo esistenza utente
    const onGoogleSignIn = (e: any) => {
        e.preventDefault();
        if (!isSigningIn) {
            setIsSigningIn(true);
            doSignInWithGoogle()
                .then(async (user: any) => {
                    // Controlla se l'utente esiste già usando firebase.localId
                    const userExists = await checkUserExists(user.uid);
                    if (userExists) {
                        // Se siamo in checkout flow, procedi con il checkout invece di solo chiudere
                        if (isCheckoutFlow && onCheckoutReady) {
                        onClose();
                            // Aspetta un attimo per assicurarsi che l'utente sia loggato
                            setTimeout(() => {
                                onCheckoutReady();
                            }, 500);
                        } else {
                        onClose();
                        }
                    } else {
                        setIsRegistration(true);
                        setIsLogin(false);
                        setSocialUser(user);
                        setSocialEmail(user.email);
                        setSocialProvider("google");
                        // Estrai firstName e lastName da Google
                        const displayName = user.displayName || "";
                        const nameParts = displayName.split(" ");
                        setSocialFirstName(nameParts[0] || "");
                        setSocialLastName(nameParts.slice(1).join(" ") || "");
                        // Estrai photoUrl da Google
                        setSocialPhotoUrl(user.photoURL || "");
                        setRegStep("social_preferences");
                        setIsSigningIn(false);
                    }
                })
                .catch(() => {
                    setIsSigningIn(false);
                });
            return false;
        }
    };

    const onAppleSignIn = (e: any) => {
        e.preventDefault();
        if (!isSigningIn) {
            setIsSigningIn(true);
            doSignInWithApple()
                .then(async (user: any) => {
                    // Controlla se l'utente esiste già usando firebase.localId
                    const userExists = await checkUserExists(user.uid);
                    if (userExists) {
                        // Se siamo in checkout flow, procedi con il checkout invece di solo chiudere
                        if (isCheckoutFlow && onCheckoutReady) {
                        onClose();
                            // Aspetta un attimo per assicurarsi che l'utente sia loggato
                            setTimeout(() => {
                                onCheckoutReady();
                            }, 500);
                        } else {
                        onClose();
                        }
                    } else {
                        setIsRegistration(true);
                        setIsLogin(false);
                        setSocialUser(user);
                        setSocialEmail(user.email);
                        setSocialProvider("apple");
                        setRegStep("social_preferences");
                        setIsSigningIn(false);
                    }
                })
                .catch(() => {
                    setIsSigningIn(false);
                });
            return false;
        }
    };


    const handleSteps = (step: string) => {
        if (step === "profile") {
            setRegStep("profile")
        } else if (step === "passwordLogin") {
            setStep("password")
        } else if (step === "resetPassword") {
            setStep("resetPassword")
        }
    }

    // Funzione per completare la registrazione social
    const completeSocialRegistration = async () => {
        if (!socialUser) return;

        // Validazione nickname
        if (!nickname || nickname.trim() === "") {
            setErrorMessage("Il nickname è obbligatorio");
            return;
        }

        // Validazione checkbox obbligatori
        if (!termsAndConditions) {
            setErrorMessage("Devi accettare i termini e condizioni");
            return;
        }

        if (!privacyPolicy) {
            setErrorMessage("Devi accettare l'informativa sulla privacy");
            return;
        }

        setIsSigningUp(true);
        setErrorMessage("");

        try {
            // Verifica se nickname è già usato
            const usedNickname = await checkNickname(nickname);
            if (usedNickname) {
                setErrorMessage("Riprova con un nickname diverso");
                setIsSigningUp(false);
                return;
            }

            // Inserisce nickname nel profilo Firebase
            await doInsertDisplayName(nickname.trim());

            // Prepara le preferenze utente
            const userPreferences = await prepareUserPreferences({
                termsAndConditions,
                privacyPolicy,
                marketingEmails,
                ambassadorProgram,
                analytics
            });

            // Inserisce utente su Hasura con le preferenze
            // Per Apple, non inviare firstName e lastName
            const firstName = socialProvider === "apple" ? null : (socialFirstName || null);
            const lastName = socialProvider === "apple" ? null : (socialLastName || null);
            const avatar = socialProvider === "google" ? (socialPhotoUrl || null) : null;
            let id = await insertUserHasura(socialEmail, nickname.trim(), firstName, lastName, userPreferences, avatar);
            if (id) {
                // Se siamo in checkout flow, mostra il dialog di successo checkout e procedi
                if (isCheckoutFlow || pathname === "/shop/checkout") {
                    setIsSigningUp(false);
                    setRegStep("checkout_success");
                    return;
                }
                // Registrazione completata
                setRegStep("confirmed");
                setIsSigningUp(false);
                onClose();
            }
        } catch (error: any) {
            setIsSigningUp(false);
            const message = getFirebaseErrorMessage(error.code);
            setErrorMessage(message);
        }
    }

    // Esempio di reset password
    const handlePasswordReset = () => {
        doPasswordReset(email);
    };

    // Switch tra login e registrazione
    const switchMode = () => {
        setIsLogin(!isLogin);
        setIsRegistration(!isRegistration);
        // Reset campi
        setEmail("");
        setPassword("");
        setNickname("");
        setFirstName("");
        setLastName("");
        setNickname("");
        setIsAdult(false);

        setErrorMessage("");
        setIsSigningIn(false);
        setIsSigningUp(false);
        // Reset step
        setStep("email");
        setRegStep("email");
    };

    // Al click su “Continua” nel secondo step (password)
    const handleRegPasswordContinue = () => {
        if (password === "" || password.length < 6) {
            setErrorMessage("La password deve essere di almeno 6 caratteri");
            return
        }
        setErrorMessage("");
        setRegStep("profile");
    };

    return (
        <div className="w-96 text-gray-600 space-y-5 p-4 shadow-xl border rounded-xl bg-white relative max-h-[90vh] overflow-y-auto">
            {/* Pulsante di chiusura */}
            <button
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 p-2 font-paragraph"
                onClick={onClose}
            >
                ✕
            </button>

            {/* ================== LOGIN ================== */}
            {
                isLogin && !isRegistration && (
                    <LoginFields isLogin={isLogin} isRegistration={isRegistration} onSubmit={onSubmit}
                        isSigningIn={isSigningIn}
                        setEmail={setEmail} email={email} onGoogleSignIn={onGoogleSignIn}
                        onAppleSignIn={onAppleSignIn}
                        step={step} errorMessage={errorMessage} handleSteps={handleSteps} switchMode={switchMode}
                        password={password} setPassword={setPassword} />
                )}

            {/* ================== REGISTRAZIONE: 5 step ================== */}
            {isRegistration && !isLogin && (
                <>
                    {/* Se siamo nei primi 3 step, usiamo <form> con onSubmit */}
                    <RegisterFields regStep={regStep} onSubmit={onSubmit}
                        isSigningIn={isSigningIn} setEmail={setEmail} email={email}
                        onGoogleSignIn={onGoogleSignIn} onAppleSignIn={onAppleSignIn}
                        errorMessage={errorMessage} handleSteps={handleSteps} switchMode={switchMode}
                        password={password} setPassword={setPassword}
                        firstName={firstName}
                        setFirstName={setFirstName} lastName={lastName} setLastName={setLastName}
                        nickname={nickname}
                        setNickname={setNickname} isAdult={isAdult} setIsAdult={setIsAdult}
                        isSigningUp={isSigningUp}
                        termsAndConditions={termsAndConditions}
                        setTermsAndConditions={setTermsAndConditions}
                        privacyPolicy={privacyPolicy}
                        setPrivacyPolicy={setPrivacyPolicy}
                        marketingEmails={marketingEmails}
                        setMarketingEmails={setMarketingEmails}
                        ambassadorProgram={ambassadorProgram}
                        setAmbassadorProgram={setAmbassadorProgram}
                        analytics={analytics}
                        setAnalytics={setAnalytics} />

                    {/* STEP SOCIAL: Preferenze e nickname per social login */}
                    {regStep === "social_preferences" && (
                        <div className="p-4">
                            <h3 className="font-heading text-gray-800 text-xl font-semibold sm:text-2xl mt-2 text-center mb-6">
                                Completa la registrazione
                            </h3>

                            <p className="font-paragraph text-sm text-gray-600 mb-4 text-center">
                                Registrato con: <span className="font-semibold">{socialEmail}</span>
                            </p>

                            {/* Campi Nome e Cognome modificabili - Solo per Google */}
                            {socialProvider === "google" && (
                                <>
                                    <div className="mb-4 font-paragraph">
                                        <label className="block text-sm text-gray-700 font-bold mb-1">
                                            Nome
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Inserisci il tuo nome"
                                            className="
                                                w-full mt-1 px-3 py-2 text-gray-700 bg-white
                                                border border-gray-300 rounded-lg
                                                focus:ring-1 focus:ring-[#EA5D39]
                                            "
                                            value={socialFirstName}
                                            onChange={(e) => setSocialFirstName(e.target.value)}
                                        />
                                    </div>

                                    <div className="mb-4 font-paragraph">
                                        <label className="block text-sm text-gray-700 font-bold mb-1">
                                            Cognome
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Inserisci il tuo cognome"
                                            className="
                                                w-full mt-1 px-3 py-2 text-gray-700 bg-white
                                                border border-gray-300 rounded-lg
                                                focus:ring-1 focus:ring-[#EA5D39]
                                            "
                                            value={socialLastName}
                                            onChange={(e) => setSocialLastName(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Campo Nome utente */}
                            <div className="mb-4 font-paragraph">
                                <label className="block text-sm text-gray-700 font-bold mb-1">
                                    Nome utente
                                </label>
                                <input
                                    type="text"
                                    required
                                    placeholder="Crea il tuo nome utente"
                                    className="
                                        w-full mt-1 px-3 py-2 text-gray-700 bg-white
                                        border border-gray-300 rounded-lg
                                        focus:ring-1 focus:ring-[#EA5D39]
                                    "
                                    value={nickname}
                                    onChange={(e) => setNickname(e.target.value)}
                                />
                            </div>

                            {/* CHECKBOX OBBLIGATORIO: Termini e Condizioni */}
                            <div className="flex items-start mt-4 font-paragraph">
                                <input
                                    id="socialTermsAndConditions"
                                    type="checkbox"
                                    className="w-4 h-4 text-[#EA5D39] border-gray-300 focus:ring-[#EA5D39] mt-1 flex-shrink-0"
                                    checked={termsAndConditions}
                                    onChange={() => setTermsAndConditions(!termsAndConditions)}
                                />
                                <label htmlFor="socialTermsAndConditions" className="ml-2 text-sm text-gray-700 cursor-pointer">
                                    Dichiaro di aver letto e accettato i{" "}
                                    <a
                                        href="/termini-e-condizioni"
                                        target="_blank"
                                        className="text-[#026E78FF] hover:underline"
                                    >
                                        termini e condizioni
                                    </a>{" "}
                                    dell&apos;app Babalù®.{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                            </div>

                            {/* CHECKBOX OBBLIGATORIO: Privacy Policy */}
                            <div className="flex items-start mt-4 font-paragraph">
                                <input
                                    id="socialPrivacyPolicy"
                                    type="checkbox"
                                    className="w-4 h-4 text-[#EA5D39] border-gray-300 focus:ring-[#EA5D39] mt-1 flex-shrink-0"
                                    checked={privacyPolicy}
                                    onChange={() => setPrivacyPolicy(!privacyPolicy)}
                                />
                                <label htmlFor="socialPrivacyPolicy" className="ml-2 text-sm text-gray-700 cursor-pointer">
                                    Dichiaro di aver letto l&apos;{" "}
                                    <a
                                        href="/privacy-policy"
                                        target="_blank"
                                        className="text-[#026E78FF] hover:underline"
                                    >
                                        informativa della privacy
                                    </a>{" "}
                                    e acconsento al trattamento dei miei dati personali per finalità legate all&apos;utilizzo dell&apos;app.{" "}
                                    <span className="text-red-500">*</span>
                                </label>
                            </div>

                            {/* CHECKBOX OPZIONALE: Marketing Emails */}
                            <div className="flex items-start mt-4 font-paragraph">
                                <input
                                    id="socialMarketingEmails"
                                    type="checkbox"
                                    className="w-4 h-4 text-[#EA5D39] border-gray-300 focus:ring-[#EA5D39] mt-1 flex-shrink-0"
                                    checked={marketingEmails}
                                    onChange={(e) => {
                                        setMarketingEmails(e.target.checked);
                                        // Se disabilito marketing emails, disabilito anche ambassador program
                                        if (!e.target.checked) {
                                            setAmbassadorProgram(false);
                                        }
                                    }}
                                />
                                <label htmlFor="socialMarketingEmails" className="ml-2 text-sm text-gray-700 cursor-pointer">
                                    Acconsento a ricevere comunicazioni promozionali e di marketing da parte di Babalù® (email, notifiche push o altri canali). Il consenso è facoltativo e revocabile in qualsiasi momento.
                                </label>
                            </div>

                            {/* CHECKBOX OPZIONALE: Ambassador Program */}
                            <div className="flex items-start mt-4 font-paragraph">
                                <input
                                    id="socialAmbassadorProgram"
                                    type="checkbox"
                                    className={`w-4 h-4 text-[#EA5D39] border-gray-300 focus:ring-[#EA5D39] mt-1 flex-shrink-0 ${!marketingEmails ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    checked={ambassadorProgram}
                                    disabled={!marketingEmails}
                                    onChange={(e) => {
                                        setAmbassadorProgram(e.target.checked);
                                        // Se accetta il programma ambassador, deve accettare anche le email di marketing
                                        if (e.target.checked) {
                                            setMarketingEmails(true);
                                        }
                                    }}
                                />
                                <label htmlFor="socialAmbassadorProgram" className={`ml-2 text-sm ${!marketingEmails ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'
                                    }`}>
                                    Voglio aderire al programma ambassador per ricevere aggiornamenti sulle campagne di promozione e contribuire alla diffusione di Babalù® come testimonial. Acconsento a essere contattato a tal fine.
                                </label>
                            </div>

                            {/* CHECKBOX OPZIONALE: Analytics */}
                            <div className="flex items-start mt-4 font-paragraph">
                                <input
                                    id="socialAnalytics"
                                    type="checkbox"
                                    className="w-4 h-4 text-[#EA5D39] border-gray-300 focus:ring-[#EA5D39] mt-1 flex-shrink-0"
                                    checked={analytics}
                                    onChange={(e) => setAnalytics(e.target.checked)}
                                />
                                <label htmlFor="socialAnalytics" className="ml-2 text-sm text-gray-700 cursor-pointer">
                                    Vuoi aiutarci a migliorare e personalizzare la tua esperienza, acconsentendo a che l&apos;app tenga traccia delle mie attività (come ricerche, acquisti o preferenze), per ottimizzare l&apos;App, condurre analisi statistiche e/o per mostrarmi contenuti su misura? <a href="/privacy-policy" target="_blank" className="primary-dark-color link-promozionali underline">Leggi l&apos;informativa</a>
                                </label>
                            </div>

                            {/* Errore eventuale */}
                            {errorMessage && (
                                <span className="font-paragraph text-red-600 font-bold block mt-4 text-center">
                                    {errorMessage}
                                </span>
                            )}

                            {/* Bottone "Completa registrazione" */}
                            <button
                                onClick={completeSocialRegistration}
                                disabled={isSigningUp}
                                className={`
                                    w-full 
                                    mt-6
                                    bg-[#026E78FF] 
                                    text-white 
                                    py-3 
                                    rounded 
                                    hover:bg-[#027E78FF]
                                    transition
                                    font-paragraph
                                    font-semibold
                                    ${isSigningUp ? "cursor-not-allowed opacity-60" : ""}
                                `}
                            >
                                {isSigningUp ? "Completando registrazione..." : "Completa registrazione"}
                            </button>
                        </div>
                    )}

                    {/* STEP 4: "Conferma l'indirizzo email" - NON mostrare se siamo in checkout flow */}
                    {regStep === "confirmation" && !isCheckoutFlow && pathname !== "/shop/checkout" && (
                        <div className="p-4 text-center">
                            <h2 className="font-heading text-xl font-semibold text-gray-800 mb-2">
                                Registrazione completata!
                            </h2>
                            <h3 className="font-heading text-base font-medium text-gray-600 mb-4">
                                Conferma l'indirizzo email
                            </h3>
                            <p className="font-paragraph text-sm text-gray-700 mb-4">
                                Abbiamo inviato un'e-mail di conferma all'indirizzo{" "}
                                <span className="font-semibold">{email}</span>.
                            </p>
                            <p className="font-paragraph text-sm text-gray-700 mb-4">
                                Non hai ricevuto la mail?
                            </p>
                            <button
                                onClick={() => {
                                    // Rispedisce la mail di verifica (facoltativo)
                                    doSendEmailVerification();
                                }}
                                className="
                  bg-[#026E78FF]
                  text-white 
                  px-4 
                  py-2 
                  rounded 
                  hover:bg-[#027E78FF]
                  transition
                  font-paragraph
                "
                            >
                                Invia di nuovo
                            </button>
                        </div>
                    )}

                    {/* STEP 5: “Account confermato!” */}
                    {regStep === "confirmed" && (
                        <div className="p-4 text-center">
                            {/* Immagine /assets/modale-account-confermato.svg */}
                            <img
                                src="/assets/modale-account-confermato.svg"
                                alt="Account confermato"
                                className="mx-auto mb-4 w-32 h-32"
                            />

                            <h2 className="font-heading text-xl font-semibold text-gray-800 mb-2">
                                Grazie!
                            </h2>
                            <h3 className="font-heading text-base font-medium text-gray-600 mb-4">
                                Account confermato!
                            </h3>

                            {/* Esempio di messaggio di benvenuto */}
                            <p className="font-paragraph text-sm text-gray-700 mb-4">
                                Ciao{" "}
                                <span className="font-semibold">
                                    {firstName || "Pet Lover"}
                                </span>
                                , ti diamo il benvenuto in Babalù®, la community italiana dei Pet
                                Lover!
                            </p>

                            {/* Bottone per chiudere la modale o fare un redirect */}
                            <button
                                onClick={onClose}
                                className="
                  bg-[#026E78FF]
                  text-white 
                  px-4 
                  py-2 
                  rounded 
                  hover:bg-[#027E78FF]
                  transition
                  font-paragraph
                "
                            >
                                Chiudi
                            </button>
                        </div>
                    )}

                    {/* STEP 6: "Checkout success" - Mostrato solo quando si registra durante il checkout */}
                    {regStep === "checkout_success" && (
                        <div className="p-4 text-center">
                            {/* Icona di successo */}
                            <div className="flex justify-center mb-4">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg
                                        className="w-10 h-10 text-green-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                </div>
                            </div>

                            <h2 className="font-heading text-xl font-semibold text-gray-800 mb-2">
                                Utente registrato correttamente
                            </h2>
                            <p className="font-paragraph text-sm text-gray-700 mb-6">
                                Procediamo con l&apos;ordine...
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default LoginComponent;
